import { useEffect, useMemo, useState } from 'react';
import type { ApolloClient, QueryOptions } from 'apollo-client';
import type { OperationVariables } from '@apollo/react-common';
import type { DocumentNode } from 'graphql';
import { useRelayEnvironment } from 'react-relay';
import { createOperationDescriptor, getRequest } from 'relay-runtime';
import type { ConcreteRequest, GraphQLTaggedNode } from 'relay-runtime';
import { useApolloClient } from '@apollo/react-hooks';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { NetworkStatus } from './NetworkStatus.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { isRelayEnabled } from './isRelayEnabled.ts';
import { getRelayOperationName } from './relayOperation.ts';
import { fetchQueryWithErrorPolicy } from './fetchQuery.ts';

type QueryNoFurtherUpdateResult<TData = any> = {
	data: TData | undefined;
	error?: ApolloErrorInterop;
	loading: boolean;
	networkStatus: NetworkStatus;
};

type UseQueryNoFurtherUpdateInteropOptions<TVariables> = Omit<
	QueryOptions<TVariables>,
	'query' | 'fetchPolicy'
> & {
	fetchPolicy?: 'standby';
	skip?: boolean;
	skipFetchIfCached?: boolean;
	client?: ApolloClient<any>;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

const queryResults = new WeakMap<
	DocumentNode,
	Map<string, Promise<QueryNoFurtherUpdateResult<any>>>
>();

// Relay uses its compiled `GraphQLTaggedNode` (a `ConcreteRequest` object) as the
// promise-cache key. Kept separate from the Apollo `DocumentNode` map above so the
// two routes never collide on identity.
const relayQueryResults = new WeakMap<
	object,
	Map<string, Promise<QueryNoFurtherUpdateResult<any>>>
>();

/**
 * This hook works like `useQuery` but it will not update the result after the initial fetch.
 * Essentially it works like fetchPolicy: "standby". See https://www.apollographql.com/docs/react/data/queries/#standby
 *
 * The difference between this hook and `useQuery` is it internally uses pure promise instead of Observable.
 * In the case a query is frequent fetched this hook performs much faster than the Apollo's useQuery.
 * If other fetchPolicy is needed, use `useQuery` instead.
 *
 * Interop: when the calling package is Relay-enabled the hook reads/fetches through the Relay store
 * (see `useQueryNoFurtherUpdateInteropRelay`); otherwise it uses Apollo. Both routes preserve the
 * same "fetch once, no further update" contract and return shape so the hook is a transparent
 * drop-in across build variants.
 */
export function useQueryNoFurtherUpdateInterop<TData = any, TVariables = OperationVariables>(
	query: DocumentNode | GraphQLTaggedNode,
	options?: UseQueryNoFurtherUpdateInteropOptions<TVariables>,
): QueryNoFurtherUpdateResult<TData | null> {
	const operationName = getRelayOperationName(query as GraphQLTaggedNode);
	if (isRelayEnabled(options?.__callerPackage__, { operationName, phase: 'component' })) {
		// eslint-disable-next-line react-hooks/rules-of-hooks -- Apollo/Relay route is branch-stable for a given caller package.
		return useQueryNoFurtherUpdateInteropRelay<TData, TVariables>(query, options);
	}

	// eslint-disable-next-line react-hooks/rules-of-hooks -- Apollo/Relay route is branch-stable for a given caller package.
	return useQueryNoFurtherUpdateInteropApollo<TData, TVariables>(query, options);
}

function useQueryNoFurtherUpdateInteropApollo<TData = any, TVariables = OperationVariables>(
	query: DocumentNode | GraphQLTaggedNode,
	options?: UseQueryNoFurtherUpdateInteropOptions<TVariables>,
): QueryNoFurtherUpdateResult<TData | null> {
	const {
		client: clientOverride,
		skipFetchIfCached,
		__callerPackage__: _callerPackage,
		...apolloQueryOptions
	} = options ?? {};
	let clientFromContext: ApolloClient<any> | undefined;
	try {
		clientFromContext = useApolloClient();
	} catch {
		// useApolloClient throws "No Apollo Client instance can be found". It is expected in some unit tests.
	}

	// technically not a hash but it's good enough since variable is an plain object.
	const variablesHash = JSON.stringify(options?.variables || {});
	const context = { ...options?.context, requestId: (query as ConcreteRequest).params?.id };
	query = toDocumentNode(query);

	const client = clientOverride || clientFromContext || window['GLOBAL_APOLLO_CLIENT']!;
	let dataFromCache: TData | null = null;
	try {
		if (!options?.skip) {
			dataFromCache = client.readQuery<TData, TVariables>({
				query,
				...apolloQueryOptions,
			});
		}
	} catch {}
	const hasCachedData = dataFromCache !== null;

	const [result, setResult] = useState<QueryNoFurtherUpdateResult<TData | null>>(() => {
		if (options?.skip) {
			return { data: null, loading: false, networkStatus: NetworkStatus.ready };
		}

		return dataFromCache
			? { data: dataFromCache, loading: false, networkStatus: NetworkStatus.ready }
			: { data: null, loading: true, networkStatus: NetworkStatus.loading };
	});

	useEffect(() => {
		if (options?.skip) return;
		if (skipFetchIfCached && hasCachedData) return;

		// If query is previously fetched but with different variables. Re-fetch the query.
		const queryFetched = queryResults.has(query) && queryResults.get(query)!.has(variablesHash);
		if (result.loading || !queryFetched) {
			// Same query + variable should reuse the same promise to avoid going through expensive Apollo client.
			let queryResultPromise: Promise<QueryNoFurtherUpdateResult<TData>>;
			if (queryFetched) {
				queryResultPromise = queryResults.get(query)?.get(variablesHash)!;
			} else {
				queryResultPromise = client.query<TData, TVariables>({
					query,
					...apolloQueryOptions,
					context,
				});
				if (!queryResults.has(query)) {
					queryResults.set(query, new Map());
				}
				// Do not set cache when running in jest otherwise test result will be affected by other tests.
				if (!process.env.JEST_WORKER_ID) {
					queryResults.get(query)!.set(variablesHash, queryResultPromise);
				}
			}

			void queryResultPromise
				.then((queryResult) => setResult(queryResult))
				.catch((e): void => {
					setResult({
						...result,
						loading: false,
						networkStatus: NetworkStatus.ready,
						error: e,
					});
				});
		}
		// When result has no data yet (e.g. after skip transitions from true to false, or
		// when the main fetch branch's setResult didn't fire due to stale closure),
		// re-attach to the cached promise to ensure data is resolved into state.
		if (!options?.skip && result.data === null) {
			const isQueryCached = queryResults.has(query) && queryResults.get(query)!.has(variablesHash);
			if (isQueryCached) {
				void queryResults
					.get(query)
					?.get(variablesHash)!
					.then((queryResult) => setResult(queryResult));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [variablesHash, options?.skip, skipFetchIfCached, hasCachedData]);

	return result;
}

/**
 * Relay route for `useQueryNoFurtherUpdateInterop`.
 *
 * Reads through the Relay environment from React context (`useRelayEnvironment`) — the same
 * environment the surrounding tree renders against — so the initial (synchronous) read is identical
 * on the server and on the client's first hydration render. That identity is what keeps SSR and
 * hydration in sync. Reading `window.GLOBAL_RELAY_ENV` (as the imperative `readQueryInterop` /
 * `queryInterop` helpers do) is NOT safe here: it is not guaranteed to be the render tree's
 * environment on the server, which produces server-empty / client-populated divergence and
 * page-wide hydration mismatches.
 *
 * Preserves the "fetch once, no further update" contract without a store subscription:
 * - initial render: `environment.lookup` of the operation; a complete (non-missing) snapshot is
 *   used as data, otherwise the hook starts in a loading state;
 * - first mount / cache miss: a single `fetchQuery`, resolved once into state.
 */
function useQueryNoFurtherUpdateInteropRelay<TData = any, TVariables = OperationVariables>(
	query: DocumentNode | GraphQLTaggedNode,
	options?: UseQueryNoFurtherUpdateInteropOptions<TVariables>,
): QueryNoFurtherUpdateResult<TData | null> {
	const { skipFetchIfCached } = options ?? {};
	const skip = options?.skip ?? false;
	const environment = useRelayEnvironment();
	const relayQuery = query as GraphQLTaggedNode;
	const variables = (options?.variables ?? {}) as Record<string, any>;
	const variablesHash = JSON.stringify(options?.variables || {});

	// Build the operation descriptor and read the current snapshot from the *context* environment.
	// A complete (non-missing) snapshot renders on the first pass — this is what SSRs real content
	// and makes the client's first render match it.
	const operation = useMemo(
		() => (skip ? null : createOperationDescriptor(getRequest(relayQuery), variables)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[relayQuery, skip, variablesHash],
	);
	const snapshot = operation ? environment.lookup(operation.fragment) : undefined;
	const hasCompleteData = Boolean(operation && snapshot && !snapshot.isMissingData);
	const dataFromCache = hasCompleteData ? (snapshot!.data as TData) : null;
	const hasCachedData = dataFromCache !== null;

	const [result, setResult] = useState<QueryNoFurtherUpdateResult<TData | null>>(() => {
		if (skip) {
			return { data: null, loading: false, networkStatus: NetworkStatus.ready };
		}

		return hasCompleteData
			? { data: dataFromCache, loading: false, networkStatus: NetworkStatus.ready }
			: { data: null, loading: true, networkStatus: NetworkStatus.loading };
	});

	useEffect(() => {
		if (skip || !operation) return;
		if (skipFetchIfCached && hasCachedData) return;

		// Reuse the in-flight/resolved promise for the same query + variables.
		const queryFetched =
			relayQueryResults.has(relayQuery) && relayQueryResults.get(relayQuery)!.has(variablesHash);
		if (result.loading || !queryFetched) {
			let queryResultPromise: Promise<QueryNoFurtherUpdateResult<TData | null>>;
			if (queryFetched) {
				queryResultPromise = relayQueryResults.get(relayQuery)!.get(variablesHash)!;
			} else {
				queryResultPromise = fetchQueryWithErrorPolicy<{
					response: TData;
					variables: Record<string, any>;
				}>(
					environment,
					relayQuery,
					variables,
					{ networkCacheConfig: { metadata: options?.context } },
					{ errorPolicy: 'all' },
				)
					.toPromise()
					.then((fetchResult) => {
						// Prefer the normalized store snapshot so the shape matches the initial read.
						const nextSnapshot = environment.lookup(operation.fragment);
						const data =
							nextSnapshot && !nextSnapshot.isMissingData
								? (nextSnapshot.data as TData)
								: ((fetchResult?.data ?? null) as TData | null);
						return {
							data,
							loading: false,
							networkStatus: NetworkStatus.ready,
							error: fetchResult?.error,
						};
					});
				if (!relayQueryResults.has(relayQuery)) {
					relayQueryResults.set(relayQuery, new Map());
				}
				// Do not set cache when running in jest otherwise test result will be affected by other tests.
				if (!process.env.JEST_WORKER_ID) {
					relayQueryResults.get(relayQuery)!.set(variablesHash, queryResultPromise);
				}
			}

			void queryResultPromise
				.then((queryResult) => setResult(queryResult))
				.catch((e): void => {
					setResult({
						...result,
						loading: false,
						networkStatus: NetworkStatus.ready,
						error: e,
					});
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [variablesHash, skip, skipFetchIfCached, hasCachedData]);

	return result;
}
